import { useMemo } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import type {
  AppThemingContextValue,
  ResponseTheme,
} from '@trinity/chat-components/containers/AppTheming/Context.js';
import {
  AppThemingContext,
} from '@trinity/chat-components/containers/AppTheming/Context.js';
import { transformPrimary, transformShade, transformColors } from './AppThemingContainer';

type Props = {
  children: React.ReactNode;
  /**
   * The theming data to provide to child components.
   * Pass the theme configuration directly instead of fetching.
   */
  theme: ResponseTheme;
  /**
   * Whether theming/whitelabeling is enabled.
   */
  enabled: boolean;
};

/**
 * A simpler alternative to AppThemingContainer for cases where
 * theming data is already available synchronously (no fetching needed).
 *
 * Use this when:
 * - You already have theming data from another source (e.g., Redux, another context)
 * - You don't need React Query's caching/refetching behavior
 *
 * Use AppThemingContainer when:
 * - You need to fetch theming data asynchronously
 * - You want React Query's caching and refetch capabilities
 *
 * @example
 * ```tsx
 * const theme = {
 *   app: { prompt: { documents: '' }, title: '' },
 *   color: { bubble: '#abc', primary: '#def', prompt: '#123' },
 *   enabled: true,
 *   logo: { horizontal: '', square: '' },
 * };
 *
 * <AppThemingProvider theme={theme} enabled={true}>
 *   <MyComponent />
 * </AppThemingProvider>
 * ```
 */
export const AppThemingProvider = (props: Props) => {
  const { theme, enabled, children } = props;

  const themingCtx = useMemo(() => {
    // Create a mock query result for backward compatibility with components
    // that check query.isFetchedAfterMount or query.data
    const mockQuery = {
      data: theme,
      isLoading: false,
      isFetching: false,
      isSuccess: true,
      isError: false,
      isFetched: true,
      isFetchedAfterMount: true,
      isStale: false,
      isPending: false,
      status: 'success' as const,
      fetchStatus: 'idle' as const,
      error: null,
      errorUpdateCount: 0,
      failureCount: 0,
      failureReason: null,
      dataUpdatedAt: Date.now(),
      errorUpdatedAt: 0,
      isPlaceholderData: false,
      isRefetching: false,
      isRefetchError: false,
      isLoadingError: false,
      refetch: () => Promise.resolve({ data: theme } as UseQueryResult<ResponseTheme>),
      promise: Promise.resolve(theme),
    } as unknown as UseQueryResult<ResponseTheme>;

    // Build palette using same transforms as AppThemingContainer
    const palette = {
      ...transformColors(theme?.color),
      primary: transformPrimary(theme?.color?.primary),
      shade: transformShade(theme?.color?.bubble),
    };

    return {
      enabled,
      query: mockQuery,
      palette,
      theme,
    } as AppThemingContextValue;
  }, [theme, enabled]);

  return (
    <AppThemingContext.Provider value={themingCtx}>
      {children}
    </AppThemingContext.Provider>
  );
};

AppThemingProvider.displayName = 'AppTheming.Provider';
